"use client";

/**
 * VOS-v2 — EmptyState padrão do sistema (ver VOS-UNO.md §"Empty states").
 *
 * ⚠️ Promovido para `@vertex/ui`; `apps/web/src/components/ui/empty-state.tsx`
 * é só um shim de reexport. A variante `hero` abaixo chegou pela main DEPOIS da
 * promoção e foi portada no merge — editar o shim não tem efeito nenhum.
 *
 * Anatomia única para TODO estado vazio (lista, busca, campo, página):
 * card de borda TRACEJADA + ícone em círculo + título bold + sub explicando
 * o que aparece ali + CTA opcional. Sem EmptyState ad-hoc: variação é só
 * ícone/copy/ação. `icon` tem default (Inbox) pra nunca renderizar vazio seco.
 *
 * DS 2.0 (Fase 3C): `icons` (leque de 3 cards que se abrem no hover — refs
 * 5/13) e `illustration` (slot editorial §36, só em contexto espaçoso).
 *
 * Variante `hero` (piloto Contatos/Empresas · 01/08/2026, referência Shopify):
 * página inteira vazia = copy + CTAs à esquerda e mosaico 2x2 de ícones à
 * direita, em card sólido (a borda tracejada segue nas demais variantes).
 */
import { type ReactNode } from "react";
import { Inbox, type LucideIcon } from "lucide-react";
import { m } from "motion/react";
import { cn } from "../lib/cn";
import { useMovimento } from "../movimento/provider";

/**
 * O leque de três ícones. `pequeno` é o do vazio DENTRO de card (`compact`,
 * 29/09): o mesmo leque, em caixas de 36 e ícone de 16, pra o card vazio do
 * Insights manter a cara do vazio da casa sem ocupar a altura de uma página.
 */
function IconFan({ icons, pequeno = false, abre = false }: { icons: [LucideIcon, LucideIcon, LucideIcon]; pequeno?: boolean; abre?: boolean }) {
  const base = cn(
    "grid place-items-center border border-[var(--line)] bg-[var(--panel)] text-[var(--ink-3)] shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-transform duration-vos-xl ease-vos motion-reduce:transition-none",
    pequeno ? "h-9 w-9 rounded-[10px]" : "h-11 w-11 rounded-xl",
  );
  const ic = pequeno ? "h-4 w-4" : "h-5 w-5";
  const [A, B, C] = icons;
  return (
    <div className={cn("isolate flex justify-center", pequeno ? "mb-3" : "mb-4", abre && "vos-es-leque-abre", abre && pequeno && "is-pequeno")} aria-hidden>
      <div className={cn(base, "vos-es-leque-esq relative left-2 top-1 -rotate-6 group-hover/es:-translate-x-2.5 group-hover/es:-translate-y-0.5 group-hover/es:-rotate-12")}>
        <A className={ic} />
      </div>
      <div className={cn(base, "z-10 group-hover/es:-translate-y-1")}>
        <B className={ic} />
      </div>
      <div className={cn(base, "vos-es-leque-dir relative right-2 top-1 rotate-6 group-hover/es:translate-x-2.5 group-hover/es:-translate-y-0.5 group-hover/es:rotate-12")}>
        <C className={ic} />
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  icons,
  illustration,
  title,
  description,
  action,
  secondaryAction,
  compact = false,
  hero = false,
  abreAoChegar = false,
  className,
}: {
  /** Ícone lucide (20-24px). Default: Inbox. */
  icon?: ReactNode;
  /** Leque de 3 ícones (abre no hover). Com `compact`, o leque pequeno. */
  icons?: [LucideIcon, LucideIcon, LucideIcon];
  /** Ilustração editorial (§36) — SÓ em contexto espaçoso, nunca em área densa. */
  illustration?: ReactNode;
  /** Texto na maioria dos casos. Aceita nó pra quando o título carrega um selo
   *  junto (ex.: "Chamadas [em breve]") — `string` continua valendo. */
  title: ReactNode;
  /** Sempre explique o que vai aparecer aqui e como fazer aparecer. Aceita nó
   *  pro caso em que o recado É um selo (ex.: "Em breve"), sem frase. */
  description?: ReactNode;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  /** Vazios DENTRO de card/painel/lista: disco menor, menos respiro, sem borda
   *  própria (o container já tem a dele). Página/seção inteira → completo. */
  compact?: boolean;
  /**
   * REGRA ÚNICA de vazio de PÁGINA/ABA (founder 02/ago/2026, ref. Shopify):
   * arte de um lado, texto + botão do outro. Sempre esse arranjo — não precisa
   * ser foto 3D, o ícone padrão serve.
   *
   * Sem `illustration`, o `icon` assume o posto num disco grande, na mesma
   * faixa vertical das artes fotográficas: assim toda tela vazia fecha na MESMA
   * altura, com ou sem arte dedicada.
   *
   * Fora da regra: vazio DENTRO de card/lista (`compact`).
   */
  hero?: boolean;
  /**
   * O LEQUE ABRE AO CHEGAR (30/09, founder: "ícones animados na hora que
   * abrir"): as caixas das pontas saem de trás da do meio e pousam no leque,
   * UMA vez, no tempo `xl` e na curva de entrada (CSS `.vos-es-leque-abre`).
   * Só com `icons`. Pra vazio que é a tela inteira de trabalho (a Conciliação
   * sem nada), não pra toda lista. O hover segue abrindo mais.
   */
  abreAoChegar?: boolean;
  className?: string;
}) {
  /* VOS Movimento: o vazio chega como um item (sobe 8px em 200). */
  const chegada = useMovimento().r.item(0);

  if (hero) {
    return (
      <m.div
        initial={chegada.initial}
        animate={chegada.animate}
        className={cn("vos-es-hero", className)}
      >
        <div className="vos-es-hero-txt">
          <h3 style={{ fontFamily: "var(--display)" }}>{title}</h3>
          {description ? <p>{description}</p> : null}
          {action || secondaryAction ? (
            <div className="vos-es-hero-cta">
              {action}
              {secondaryAction}
            </div>
          ) : null}
        </div>
        {/* Sem arte dedicada o ícone assume o posto — o arranjo nunca muda. */}
        <div className="vos-es-hero-art" aria-hidden>
          {illustration ?? <span className="vos-es-hero-ic">{icon ?? <Inbox />}</span>}
        </div>
      </m.div>
    );
  }

  return (
    <m.div
      initial={chegada.initial}
      animate={chegada.animate}
      className={cn(
        "group/es flex flex-col items-center justify-center text-center",
        compact
          ? "px-4 py-6"
          : "rounded-xl border border-dashed border-[var(--line-2)] bg-[var(--panel)] px-6 py-9",
        className,
      )}
    >
      {illustration ? (
        <div className="mb-4 max-w-[240px]" aria-hidden>
          {illustration}
        </div>
      ) : icons ? (
        <IconFan icons={icons} pequeno={compact} abre={abreAoChegar} />
      ) : (
        <div
          className={cn(
            "flex items-center justify-center rounded-full bg-[var(--panel-3)] text-[var(--ink-3)]",
            compact ? "mb-2.5 h-9 w-9" : "mb-4 h-14 w-14",
          )}
        >
          {icon ?? <Inbox className={compact ? "h-4 w-4" : "h-6 w-6"} aria-hidden />}
        </div>
      )}
      {/* Sem `capitalize` (31/08): a caixa mora na STRING desde 29/08, e este
          text-transform escapou daquela limpeza. Ele reescrevia todo título de
          vazio do produto em Title Case, então "Sem regra de comissão" chegava
          na tela como "Sem regra de comissão", contra a própria regra. */}
      <h3
        className={cn(
          "font-bold tracking-tight text-[var(--ink)]",
          compact ? "text-[length:var(--fs-body)]" : "text-[length:var(--fs-h3,19px)]",
        )}
        style={{ fontFamily: "var(--display)" }}
      >
        {title}
      </h3>
      {description && (
        <p
          className={cn(
            "max-w-md text-[var(--ink-3)]",
            compact ? "mt-1 text-[length:var(--fs-label)] leading-5" : "mt-2 text-[length:var(--fs-sm)] leading-6",
          )}
        >
          {description}
        </p>
      )}
      {action || secondaryAction ? (
        <div className={cn("flex items-center gap-2", compact ? "mt-4" : "mt-6")}>
          {secondaryAction}
          {action}
        </div>
      ) : null}
    </m.div>
  );
}
